import { dateTime } from '@orgazone/date-format';
import { parseMinor } from '@orgazone/money-format';
import type { ColKind } from '../api';

export const numericKind = (kind: ColKind) => ['money', 'fine', 'rate', 'int'].includes(kind);
export const HINT: Partial<Record<ColKind, string>> = { money: 'centimes → €', fine: '×10000 → €', rate: 'points de base → %', epoch: 'epoch → date', bool: '0/1 → oui/non' };

export function human(value: string | null, kind: ColKind): string {
  if (value === null) return 'NULL';
  if (value === '') return '(vide)';
  const n = Number(value);
  if (kind === 'bool') return value === '1' || value === 'true' ? 'oui' : value === '0' || value === 'false' ? 'non' : value;
  if (!Number.isFinite(n)) return value;
  if (kind === 'money' || kind === 'fine' || kind === 'rate') {
    const digits = kind === 'fine' ? 4 : 2;
    return (n / 10 ** digits).toLocaleString('fr-FR', { minimumFractionDigits: kind === 'money' ? 2 : 0, maximumFractionDigits: digits }) + (kind === 'rate' ? ' %' : ' €');
  }
  if (kind === 'epoch') {
    if (n === 0) return 'Non renseigné';
    return Number.isNaN(new Date(n).getTime()) ? value : dateTime(new Date(n));
  }
  return value;
}

export type CorrectionOp = 'set' | 'prefix' | 'mul';
export function correctionValue(value: string, kind: ColKind, op: CorrectionOp, readable: boolean): { value: string; error?: string } {
  if (!value.trim()) return { value: '', error: 'Saisissez une valeur.' };
  if (op === 'mul') {
    const normalized = value.trim().replace(',', '.');
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized) || !Number.isFinite(Number(normalized))) return { value: '', error: 'Saisissez un coefficient valide, par exemple 1,05.' };
    return { value: normalized };
  }
  if (op === 'set' && readable && ['money', 'fine', 'rate'].includes(kind)) {
    const parsed = parseMinor(value, { scale: kind === 'fine' ? 4 : 2 });
    return parsed.state === 'ok' ? { value: String(parsed.minor) } : { value: '', error: parsed.state === 'rejected' ? parsed.reason : 'Saisissez une valeur.' };
  }
  return { value };
}

// Même opération brute que le serveur : l'aperçu porte uniquement sur la sélection transmise.
export function computeAfter(before: string | null, op: CorrectionOp, value: string): string | null {
  if (op === 'set') return value;
  if (op === 'prefix') return value + (before ?? '');
  if (before === null || before.trim() === '' || !Number.isFinite(Number(before))) return before;
  const n = Number(before) * Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : before;
}

export function readPreference<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? fallback; } catch { return fallback; }
}
export function savePreference(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Préférences facultatives en navigation privée. */ }
}
